import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { User, UserPreferences } from '../types'

interface UserState {
  // User data
  user: User | null
  setUser: (user: User | null) => void
  
  // Authentication
  isAuthenticated: boolean
  token: string | null
  setToken: (token: string | null) => void
  login: (user: User, token: string) => void
  logout: () => void
  
  // Preferences
  preferences: UserPreferences
  updatePreferences: (prefs: Partial<UserPreferences>) => void
  setDefaultStyle: (style: string) => void
  toggleNotifications: () => void
  toggleAutoSave: () => void
  
  // Loading state
  isLoading: boolean
  setIsLoading: (loading: boolean) => void
}

const defaultPreferences: UserPreferences = {
  defaultStyle: 'casual',
  theme: 'dark',
  notifications: true,
  autoSave: true,
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      token: null,
      preferences: defaultPreferences,
      isLoading: false,
      
      setUser: (user) => set({ user }),
      
      setToken: (token) => set({ token, isAuthenticated: !!token }),
      
      login: (user, token) => {
        localStorage.setItem('token', token)
        set({ user, token, isAuthenticated: true })
      },
      
      logout: () => {
        localStorage.removeItem('token')
        set({ user: null, token: null, isAuthenticated: false })
      },
      
      updatePreferences: (prefs) => set((state) => ({
        preferences: { ...state.preferences, ...prefs }
      })),
      
      setDefaultStyle: (style) => get().updatePreferences({ defaultStyle: style }),
      
      toggleNotifications: () => set((state) => ({
        preferences: { ...state.preferences, notifications: !state.preferences.notifications }
      })),
      
      toggleAutoSave: () => set((state) => ({
        preferences: { ...state.preferences, autoSave: !state.preferences.autoSave }
      })),
      
      setIsLoading: (loading) => set({ isLoading: loading }),
    }),
    {
      name: 'user-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        token: state.token,
        preferences: state.preferences,
      }),
    }
  )
)
